/**
 * The kind of task at a glance: its glyph, in its own ink, on a neutral tile. See the stylesheet for
 * the canon and the colour rule.
 *
 * `role="img"` with the kind's name, so a screen reader hears what the colour says. Where the name
 * is printed in words right beside it — a card's own heading — pass `decorative` and the tile is
 * hidden instead, or the kind is announced twice.
 *
 * `/zadania`'s index draws the same tile, and once a row is answered it puts the answer's mark on
 * it in place of the kind's glyph, named by the answer: `children` and `label` are that.
 */

import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { AttentionItem } from '../model/attentionItems';
import { TaskGlyphMark } from './HomeGlyphs';
import styles from './reasonTile.module.css';

interface ReasonTileProps {
  readonly item: Pick<AttentionItem, 'reason' | 'glyph' | 'titleKey'>;
  readonly size: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  /** The Pulpit list's tinted tile. */
  readonly wash?: boolean;
  readonly decorative?: boolean;
  readonly className?: string;
  /** The accessible name, where it is not the kind's own. */
  readonly label?: string;
  /** What stands on the tile in place of the kind's glyph. */
  readonly children?: ReactNode;
}

export function ReasonTile({
  item,
  size,
  wash = false,
  decorative = false,
  className,
  label,
  children,
}: ReasonTileProps) {
  const { t } = useTranslation();
  return (
    <span
      className={`${String(styles.tile)} ${String(styles[size])} ${String(styles[item.reason] ?? '')} ${
        wash ? String(styles.wash) : ''
      } ${className ?? ''}`}
      {...(decorative ? { 'aria-hidden': true } : { role: 'img', 'aria-label': label ?? t(item.titleKey) })}
    >
      {children ?? <TaskGlyphMark className={styles.mark} glyph={item.glyph} />}
    </span>
  );
}
